import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {DataObjectAssignmentType, MaterialOutputType} from "@generated"
import {
    DataObjectDetailsForNameAssetFromSchemaServiceFragment,
    DataObjectDetailsForNameAssetFromSchemaServiceFragment as DataObjectDetails,
    MaterialDetailsForNameAssetFromSchemaServiceFragment as MaterialDetails,
} from "@app/common/services/name-asset-from-schema/name-asset-from-schema.generated"
import {PbrExport} from "@cm/material-maps-exporter"
import {DataObjectDefinitions, MediaType} from "@cm/utils/data-object"
import {MaterialOutputsFilenames} from "@cm/material-outputs-filenames"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {
    MaterialForNameAssetFromSchemaServiceGQL,
    NameAssetFromSchemaUpdateDataObjectGQL,
} from "@common/services/name-asset-from-schema/name-asset-from-schema.generated"
import {FlatOption} from "@labels"
import {DataResolverService} from "@material-editor/services/data-resolver.service"
import {extensionForContentType} from "@cm/utils"
import {TileableMaterialPostRenderStepInput} from "@cm/job-nodes"

function getRelatedDataObjectForFormatAndResolution(
    dataObject: DataObjectDetails,
    format: MaterialOutputsFilenames.ImageFormat,
    resolution: MaterialOutputsFilenames.ImageResolution,
) {
    if (dataObject.related === undefined) throw Error(`Expected related data objects to be present`)
    const format_ = format === "tif" ? "tiff" : format // derived data objects filenames contain "tiff" instead of "tif"

    return dataObject.related.find((related) => {
        return (
            MaterialOutputsFilenames.ensureValidMediaType(related.mediaType) === format &&
            related.objectName.endsWith(resolution === "original" ? `-${format_}` : `-${format_}-${resolution.slice("px".length)}`)
        )
    })
}

@Injectable({
    providedIn: "root",
})
export class NameAssetFromSchemaService {
    private readonly materialForNameAssetFromSchemaService = inject(MaterialForNameAssetFromSchemaServiceGQL)
    private readonly nameAssetFromSchemaUpdateDataObject = inject(NameAssetFromSchemaUpdateDataObjectGQL)
    private readonly dataResolver = inject(DataResolverService)
    private readonly logger: CmLogger = inject(CM_LOGGER_MATERIALS)

    /** The fetch half of the prefix assembly; the assembly itself is shared with the backend. */
    private async getPartialAssetData(material: MaterialDetails) {
        return MaterialOutputsFilenames.partialAssetDataForMaterial(material, this.dataResolver.resolve, this.logger)
    }

    async renameRenderFromSchema(materialId: string, assetType: DataObjectAssignmentType) {
        if (!MaterialOutputsFilenames.isAssignmentTypeValidAssetType(assetType) || !MaterialOutputsFilenames.isRenderAssetType(assetType))
            throw Error(`Invalid asset type: ${assetType}, expected render asset type`)

        const material = await fetchThrowingErrors(this.materialForNameAssetFromSchemaService)({
            materialId,
            dataObjectAssignmentFilter: {assignmentType: [assetType]},
            includeRelated: true,
        }).then(({material}) => material)

        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
            ...partialAssetData,
            type: assetType,
            imageFormat: "exr",
            imageResolution: "original",
        }

        const newFilename = MaterialOutputsFilenames.getFilenameForAssetData(assetData)
        // console.log(`newFilename: ${newFilename}`)
        if (newFilename === null) return

        // filenames for the derived images
        const pending: Promise<DataObjectDetailsForNameAssetFromSchemaServiceFragment>[] = []
        DataObjectDefinitions.DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET.forEach((info) => {
            const derivedDataObjectForInfo = getRelatedDataObjectForFormatAndResolution(
                material.dataObjectAssignments[0].dataObject,
                info.format,
                info.resolution,
            )
            if (derivedDataObjectForInfo === null) throw Error(`Derived data object for format ${info.format} and resolution ${info.resolution} not found`)

            const derivedImageAssetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
                ...assetData,
                imageFormat: info.format,
                imageResolution: info.resolution,
            }

            const derivedImageNewFilename = MaterialOutputsFilenames.getFilenameForAssetData(derivedImageAssetData)
            // console.log(`derived image newFilename ${info.format}/${info.resolution}: ${derivedImageNewFilename}`)
            if (derivedImageNewFilename === null) return

            pending.push(
                mutateThrowingErrors(this.nameAssetFromSchemaUpdateDataObject)({
                    input: {id: derivedDataObjectForInfo!.id, originalFileName: derivedImageNewFilename},
                }).then(({updateDataObject}) => updateDataObject),
            )
        })

        pending.push(
            mutateThrowingErrors(this.nameAssetFromSchemaUpdateDataObject)({
                input: {id: material.dataObjectAssignments[0].dataObject.id, originalFileName: newFilename},
            }).then(({updateDataObject}) => updateDataObject),
        )
        await Promise.all(pending)
    }

    async renameExportFromSchema(materialId: string, exportType: MaterialOutputType) {
        if (!PbrExport.Types.isPbrExportMaterialOutputType(exportType)) throw Error(`Invalid export type: ${exportType}, expected PBR export type`)

        const material = await fetchThrowingErrors(this.materialForNameAssetFromSchemaService)({
            materialId,
            includeRelated: false,
        }).then(({material}) => material)

        const outputForExportType = material.outputs.find((output) => output.type === exportType)
        if (!outputForExportType) throw Error(`Output for export type ${exportType} expected but not found for material ${materialId}`)

        // An export is named from the conversion it was configured by, so a config that does not
        // resolve to exactly one, with all five settings, cannot be named. Refused here rather than
        // inside the shared package, which cannot say which material and export type it was handed.
        const exportConfig = PbrExport.Config.parseStoredPbrExportConfig(outputForExportType.config?.content)
        if (exportConfig === undefined)
            throw Error(
                `Invalid export config for material ${materialId} and export type ${exportType}: expected exactly one conversion with all five settings`,
            )

        const outputResultDataObjectId = outputForExportType.result?.id
        if (!outputResultDataObjectId) throw Error(`Output result data object id missing for material ${materialId} and export type ${exportType}`)

        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData = {
            type: DataObjectAssignmentType.MaterialMapsExport,
            ...partialAssetData,
            exportSettings: PbrExport.Config.pbrExportSettingsForConfig(exportConfig),
        }
        const newExportName = MaterialOutputsFilenames.getFilenameForAssetData(assetData)
        if (newExportName === null) return

        await mutateThrowingErrors(this.nameAssetFromSchemaUpdateDataObject)({
            input: {id: outputResultDataObjectId, originalFileName: `${newExportName}.zip`},
        })
    }

    private defaultFilenameForDerivedImage<T extends (typeof DataObjectDefinitions.DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET)[number]>(
        format: T["format"],
        resolution: T["resolution"],
        nameBase: string,
    ) {
        const suffixResolution = resolution === "original" ? "" : resolution.replace("px", "-")
        const extension = format === "jpg" ? "jpg" : "tiff"
        const suffix = `-${extension}${suffixResolution}`
        return `${nameBase}${suffix}.${extension}`
    }

    private async getRenderAssetNameSetForMaterialAndType(materialId: string, assetType: MaterialOutputsFilenames.RenderAssetType, defaultNameBase: string) {
        const material = await fetchThrowingErrors(this.materialForNameAssetFromSchemaService)({
            materialId,
            dataObjectAssignmentFilter: {assignmentType: [assetType]},
            includeRelated: true,
        }).then(({material}) => material)

        const partialAssetData = await this.getPartialAssetData(material)
        const assetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
            type: assetType,
            ...partialAssetData,
            imageFormat: "exr",
            imageResolution: "original",
        }

        const filename = MaterialOutputsFilenames.getFilenameForAssetData(assetData) ?? `${defaultNameBase}.exr`

        const derivedImagesFilenames = DataObjectDefinitions.DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET.map((info) => {
            const derivedImageAssetData: MaterialOutputsFilenames.AssetData<MaterialOutputsFilenames.RenderAssetType> = {
                ...assetData,
                imageFormat: info.format,
                imageResolution: info.resolution,
            }

            const derivedImageNewFilename =
                MaterialOutputsFilenames.getFilenameForAssetData(derivedImageAssetData) ??
                this.defaultFilenameForDerivedImage(info.format, info.resolution, defaultNameBase)
            // console.log(`derived image newFilename ${info.format}/${info.resolution}: ${derivedImageNewFilename}`)

            return {
                format: info.format,
                resolution: info.resolution,
                filename: derivedImageNewFilename,
            }
        })

        return {
            mainImageFilename: filename,
            derivedImagesFilenames: derivedImagesFilenames as unknown as DataObjectDefinitions.DerivedImageFilenameWithFormatAndResolutionSet,
        }
    }

    async getMaterialTileableRenderNameSet(materialId: string, defaultNameBase: string) {
        return this.getRenderAssetNameSetForMaterialAndType(materialId, DataObjectAssignmentType.MaterialTileableRender, defaultNameBase)
    }

    async getMaterialThumbnailNameSet(materialId: string, assetType: FlatOption["dataObjectAssignmentType"], defaultNameBase: string) {
        if (!MaterialOutputsFilenames.isAssignmentTypeValidAssetType(assetType) || !MaterialOutputsFilenames.isRenderAssetType(assetType))
            throw Error(`Invalid asset type: ${assetType}, expected render asset type`)
        return this.getRenderAssetNameSetForMaterialAndType(materialId, assetType, defaultNameBase)
    }

    async getMaterialFabricOverviewNameSet(
        _materialId: string,
        defaultNameBase: string,
        mediaType: MediaType,
    ): Promise<TileableMaterialPostRenderStepInput["filenames"]> {
        const mainImageFilename = `${defaultNameBase}.${extensionForContentType(mediaType)}`
        return {
            mainImageFilename,
            derivedImagesFilenames: null,
        }
    }
}
